import type { Translations } from './types';

export const it: Translations = {
  nav: {
    dashboard: 'Pannello',
    subscription: 'Abbonamento',
    usage: 'Utilizzo',
    analytics: 'Analisi',
    settings: 'Impostazioni',
  },
  languageToggle: {
    label: 'EN',
    accessibilityLabel: "Passa all'inglese",
  },
  dashboard: {
    title: 'Pannello',
    welcomeTitle: 'Benvenuto su Aisthetix Virtual Try-On',
    welcomeBody:
      'Inizia selezionando un piano di abbonamento per abilitare la prova virtuale nel tuo negozio.',
    choosePlan: 'Scegli un Piano',
    currentPlan: 'Piano Attuale',
    status: 'Stato',
    unknown: 'Sconosciuto',
    usageSummary: 'Riepilogo Utilizzo',
    tryOnsUsed: (used, total) => `${used} di ${total} prove utilizzate`,
    quotaExhausted: 'Limite mensile raggiunto',
    quotaExhaustedBody:
      'Hai esaurito tutte le prove di questo mese. La prova virtuale \u00E8 in pausa per i tuoi clienti fino al prossimo ciclo di fatturazione o finch\u00E9 non aggiorni il piano.',
    approachingLimit: (remaining) =>
      `Stai per raggiungere il limite mensile \u2014 solo ${remaining} prove rimanenti.`,
    upgradeNow: 'Aggiorna Piano',
    daysRemaining: (days) =>
      `${days} giorni rimanenti nel periodo di fatturazione`,
    noUsageData: 'Nessun dato di utilizzo disponibile',
    quickActions: 'Azioni Rapide',
    manageSubscription: 'Gestisci Abbonamento',
    viewDetailedUsage: 'Visualizza Utilizzo Dettagliato',
    billingPeriod: 'Periodo di Fatturazione',
    startDate: 'Data Inizio',
    endDate: 'Data Fine',
    trialProgress: (used, cap) => `Prova gratuita: ${used} di ${cap} prove utilizzate`,
    overageActive: 'Prove aggiuntive attive',
    overageActiveBody: (price, cap) =>
      `Le prove incluse sono esaurite, quindi la prova virtuale continua a ${price} per ogni prova aggiuntiva. Non ti verrà addebitato più di ${cap} in questo periodo di fatturazione.`,
    freePlanTitle: 'Piano gratuito',
    freePlanBody: (count) =>
      `Il tuo negozio riceve ${count} prove gratuite ogni mese. Passa a un piano a pagamento per continuare a usare la prova virtuale quando le esaurisci.`,
    freePlanExhausted: 'Prove gratuite esaurite',
    freePlanExhaustedBody: (count) =>
      `Hai utilizzato tutte le ${count} prove gratuite di questo mese. La prova virtuale è in pausa per i tuoi clienti fino al mese prossimo o finché non scegli un piano.`,
    sizeCoverageTitle: 'Alcuni prodotti non possono ricevere un consiglio taglia',
    sizeCoverageGuideMismatch: (guideName) =>
      `Le taglie di «${guideName}» non combaciano con nessuno dei prodotti a cui è assegnata.`,
    sizeCoverageUncovered: (count) => `${count} prodotti recenti non sono coperti da nessuna guida taglie.`,
    sizeCoverageAction: 'Rivedi le guide taglie',
    sizeCoverageCheckIncomplete: "L'ultimo controllo di copertura non è terminato. Alcune guide potrebbero non essere ancora aggiornate.",
  },
  subscription: {
    title: 'Piani di Abbonamento',
    cancelSubscription: 'Annulla Abbonamento',
    keepSubscription: 'Mantieni Abbonamento',
    cancelModalTitle: 'Annulla Abbonamento',
    cancelModalBody:
      'Sei sicuro di voler annullare il tuo abbonamento? Questo comporter\u00E0:',
    cancelModalList: [
      'Disattivazione delle funzionalit\u00E0 di prova virtuale per il tuo negozio',
      'Interruzione di tutti gli addebiti ricorrenti',
      "Rimozione dell'accesso alle funzionalit\u00E0 premium",
    ],
    cancelModalReactivate:
      'Puoi riattivare il tuo abbonamento in qualsiasi momento.',
    currentPlanBanner: (tier) =>
      `Sei attualmente abbonato al piano ${tier}.`,
    subscriptionActive: ' Il tuo abbonamento \u00E8 attivo.',
    subscriptionStatus: (status) => ` Stato: ${status}`,
    subscriptionInactiveBanner: (tier, status) =>
      `Il tuo abbonamento ${tier} precedente \u00E8 ${status.toLowerCase()}. Scegli un piano qui sotto per iniziare un nuovo abbonamento.`,
    freeEnrolled: 'Sei sul piano gratuito',
    freeEnrolledBody: (count) =>
      `Il tuo negozio riceve ${count} prove al mese senza costi. Scegli un piano a pagamento quando ti serve di pi\u00F9.`,
    overageExplainer:
      'I piani a pagamento continuano a servire i clienti oltre le prove incluse, con un addebito per ogni prova aggiuntiva fino a un tetto massimo che approvi tu. Non ti verr\u00E0 mai addebitato oltre quel tetto.',
  },
  billingProfile: {
    title: 'Dati di fatturazione',
    description:
      'Necessari per emettere fatture corrette. I piani a pagamento restano bloccati finché l’identità fiscale dell’attività non è completa.',
    complete: 'Completi',
    required: 'Obbligatori',
    businessType: 'Tipo di attività',
    company: 'Società',
    soleTrader: 'Ditta individuale / professionista',
    legalNameCompany: 'Ragione sociale',
    legalNameSoleTrader: 'Nome e cognome legali',
    taxId: 'Partita IVA / VAT ID / EIN / identificativo fiscale',
    addressLine1: 'Indirizzo di fatturazione',
    addressLine2: 'Riga indirizzo 2',
    city: 'Città',
    region: 'Stato / provincia / regione',
    postalCode: 'Codice postale',
    countryCode: 'Codice Paese',
    countryHelp: 'Usa il codice ISO di due lettere, per esempio US, IT o FR.',
    save: 'Salva dati di fatturazione',
    saving: 'Salvataggio…',
    saved: 'Dati salvati. Ora puoi scegliere un piano a pagamento.',
    invalid: 'Completa tutti i campi obbligatori. Il Paese deve avere un codice ISO di due lettere.',
    loadError: 'Impossibile caricare i dati di fatturazione.',
    saveError: 'Impossibile salvare i dati di fatturazione.',
    paidPlanBlocked: 'Completa e salva i dati di fatturazione prima di scegliere un piano a pagamento.',
  },
  usage: {
    title: 'Statistiche di Utilizzo',
    noSubscriptionTitle: 'Nessun Abbonamento Attivo',
    noSubscriptionBody:
      'Hai bisogno di un abbonamento attivo per visualizzare le statistiche di utilizzo. Scegli un piano per iniziare.',
    viewPlans: 'Visualizza Piani',
    noUsageData:
      'Nessun dato di utilizzo disponibile per il periodo di fatturazione corrente.',
    quotaExhausted: 'Limite mensile raggiunto',
    quotaExhaustedBody:
      'Hai esaurito tutte le prove di questo mese. La prova virtuale \u00E8 in pausa per i tuoi clienti fino al prossimo ciclo di fatturazione o finch\u00E9 non aggiorni il piano.',
    approachingLimit: (remaining) =>
      `Solo ${remaining} prove rimanenti questo mese. Considera di aggiornare il piano.`,
    upgradeNow: 'Aggiorna Piano',
    usageBreakdown: 'Dettaglio Utilizzo',
    billingPeriod: 'Periodo di Fatturazione',
    startDate: 'Data Inizio',
    endDate: 'Data Fine',
    daysRemaining: 'Giorni Rimanenti',
    days: (count) => `${count} giorni`,
    currentPlanDetails: 'Dettagli Piano Attuale',
    plan: 'Piano',
    monthlyPrice: 'Prezzo Mensile',
    metric: 'Metrica',
    count: 'Conteggio',
    includedTryOns: 'Prove Incluse',
    usedTryOns: 'Prove Utilizzate',
    remainingTryOns: 'Prove Rimanenti',
    trialTitle: 'Prova Gratuita',
    trialProgress: (used, cap) => `${used} di ${cap} prove della prova gratuita utilizzate`,
    overageTitle: 'Prove Aggiuntive',
    overageActiveBody: (price) =>
      `Le prove incluse sono esaurite. La prova virtuale continua a ${price} per ogni prova aggiuntiva, fino al tetto massimo che hai approvato.`,
    overageTryOns: 'Prove Aggiuntive Addebitate',
    overageSpend: 'Costo Prove Aggiuntive',
    overageCap: 'Il Tuo Tetto Massimo',
    freePlanTitle: 'Piano Gratuito',
    freePlanBody: (count) =>
      `Il tuo negozio riceve ${count} prove al mese senza costi. Scegli un piano a pagamento per avere più prove e continuare a servire i clienti quando quelle gratuite finiscono.`,
  },
  analytics: {
    title: 'Efficacia di Aisthetix',
    subtitle: (days) => `Ultimi ${days} giorni`,
    correlationalDisclaimer:
      'Questi dati confrontano chi ha usato Try-On o Trova la mia taglia con chi non ha usato Aisthetix. Indicano una correlazione, non la prova che Aisthetix abbia causato un acquisto.',
    funnelTitle: 'Imbuto clienti',
    funnelHelp: 'Clienti distinti che hanno raggiunto ogni fase nel periodo.',
    stage: 'Fase',
    shoppers: 'Clienti',
    shopperProductPairs: 'Coppie cliente/prodotto',
    stageViewed: 'Ha visto un prodotto',
    stageTriedOn: 'Ha usato la prova virtuale',
    stageSized: 'Ha usato Trova la mia taglia',
    stageUsedAisthetix: 'Ha usato Aisthetix',
    stageAddedToCart: 'Aggiunto al carrello',
    stagePurchased: 'Ha acquistato',
    usageTitle: 'Utilizzo di Aisthetix',
    usageHelp: 'Coppie cliente/prodotto distinte. Solo Try-On, solo Trova la mia taglia ed entrambe sono categorie esclusive.',
    tryOnOnly: 'Solo Try-On',
    findMySizeOnly: 'Solo Trova la mia taglia',
    bothFeatures: 'Entrambe',
    usedAisthetix: 'Totale Aisthetix',
    comparisonTitle: 'Ha usato Aisthetix vs non ha usato Aisthetix',
    usedLabel: 'Ha usato Aisthetix',
    didNotUseLabel: 'Non ha usato Aisthetix',
    comparisonResult: (points) => `${points >= 0 ? '+' : ''}${points.toFixed(1).replace('.', ',')} punti percentuali`,
    ratioUnavailable: 'Il rapporto tra i tassi non è definito perché il gruppo di confronto non ha avuto acquisti; i tassi normalizzati restano visibili.',
    liftTitle: 'Incremento con la prova virtuale',
    liftHeadline: (multiplier) =>
      `Chi usa la prova virtuale acquista lo stesso prodotto ${multiplier}× più spesso`,
    liftDetail: (tryOnPct, baselinePct) =>
      `Il ${tryOnPct}% di chi ha usato la prova virtuale ha acquistato il prodotto provato, contro il ${baselinePct}% di chi l’ha visto senza provarlo.`,
    liftCausalCaveat:
      'È una correlazione, non una prova di causa: chi prova virtualmente potrebbe già essere più propenso ad acquistare.',
    collectingData: (minShoppers) =>
      `Raccolta dati in corso — servono almeno ${minShoppers} coppie cliente/prodotto in entrambi i gruppi prima di mostrare il grafico comparativo.`,
    revenueTitle: 'Ricavi attribuiti',
    attributedRevenue: 'Ricavi attribuiti',
    attributedOrders: 'Ordini attribuiti',
    revenueHelp:
      'Ricavi da ordini con un prodotto per cui il cliente ha usato Try-On o Trova la mia taglia, acquistato entro 30 giorni. Ogni ordine viene contato una volta.',
    loadError: 'Impossibile caricare le statistiche. Riprova.',
  },

  reviewPrompt: {
    title: 'La prova virtuale sta generando ricavi',
    body: 'Qui ti scrive direttamente Davide, che sviluppa Aisthetix. Il tuo negozio ha le prime vendite attribuite alla prova virtuale. Se Aisthetix ti è utile, una recensione sullo Shopify App Store mi dice cosa funziona e su cosa concentrarmi. Ci vuole meno di un minuto.',
    leaveReview: 'Lascia una recensione',
    maybeLater: 'Più tardi',
    dismiss: 'Chiudi',
    unavailable:
      'Il modulo per la recensione non è disponibile ora. Puoi recensire Aisthetix sullo Shopify App Store in qualsiasi momento.',
  },

  planCard: {
    active: 'Attivo',
    perMonth: 'al mese',
    tryOnsIncluded: (count) => `${count} prove incluse`,
    features: 'Funzionalit\u00E0:',
    currentPlan: 'Piano Attuale',
    upgrade: 'Aggiorna',
    downgrade: 'Declassa',
    subscribe: 'Abbonati',
    trialBadge: (days) => `${days} giorni di prova gratuita`,
    chargedEvery: (days) => `Poi addebitato ogni ${days} giorni`,
    freeBadge: 'Gratis per sempre',
    freePrice: '$0.00 USD',
    startFree: 'Inizia gratis',
    noCardRequired: 'Nessuna carta di credito richiesta',
    trialCapNote: (cap) => `La prova gratuita include fino a ${cap} prove`,
    overageLine: (price) => `Poi ${price} per ogni prova aggiuntiva`,
    overageCapNote: (cap) =>
      `Massimo ${cap} per periodo di fatturazione, e il tetto lo decidi tu`,
    freeStopsAtLimit: 'La prova virtuale si ferma quando le prove gratuite finiscono',
  },
  usageProgress: {
    title: 'Utilizzo Prove',
    used: (count) => `Utilizzate: ${count}`,
    included: (count) => `Incluse: ${count}`,
    remaining: (count) => `Rimanenti: ${count}`,
  },
  settings: {
    title: 'Impostazioni',
    rateLimitingTitle: 'Limite per Utente',
    rateLimitingDescription:
      'Limita il numero di prove virtuali che ogni visitatore pu\u00F2 effettuare in un intervallo di tempo. Questo aiuta a controllare l\'utilizzo e prevenire abusi.',
    enableRateLimiting: 'Abilita limite per utente',
    maxGenerations: 'Prove massime per visitatore',
    maxGenerationsHelp: 'Quante prove ogni visitatore pu\u00F2 effettuare prima di essere temporaneamente bloccato (1-100).',
    cooldownPeriod: 'Periodo di attesa',
    cooldownCustom: 'Durata personalizzata',
    cooldownMinutes: 'minuti',
    exampleText: (max, cooldown) =>
      `Ogni visitatore pu\u00F2 provare fino a ${max} capi. Dopo aver raggiunto il limite, deve attendere ${cooldown} prima di riprovare.`,
    save: 'Salva impostazioni',
    saveSuccess: 'Impostazioni salvate con successo',
    saveError: 'Errore nel salvataggio delle impostazioni',
    cooldown30min: '30 minuti',
    cooldown1hr: '1 ora',
    cooldown2hr: '2 ore',
    cooldown3hr: '3 ore',
    cooldown6hr: '6 ore',
    cooldown12hr: '12 ore',
    cooldown24hr: '24 ore',
    cooldownCustomLabel: 'Personalizzato',
    fitPreferenceTitle: 'Vestibilità predefinita',
    fitPreferenceDescription:
      'Scegli la vestibilità predefinita per le taglie consigliate. Gli override di collezione qui sotto hanno la precedenza quando applicabili.',
    fitPreferenceLabel: 'Vestibilità',
    fitPreferenceHelp: 'Aderente favorisce una taglia più vicina al corpo; comoda una taglia più ampia.',
    fitPreferenceTight: 'Aderente',
    fitPreferenceRegular: 'Regolare',
    fitPreferenceLoose: 'Comoda',
    fitOverridesTitle: 'Vestibilità per collezione',
    fitOverridesHelp: 'La vestibilità globale sopra si applica a ogni collezione senza override. Se un prodotto appartiene a più collezioni con override, vince il numero di priorità più basso; a parità si usa l’ID della collezione.',
    fitOverridesAdd: 'Aggiungi override collezione',
    fitOverridesEmpty: 'Nessun override. La vestibilità globale vale ovunque.',
    fitOverridesPriority: (category) => `Priorità per ${category}`,
    fitOverridesPreference: (category) => `Vestibilità per ${category}`,
    fitOverridesRemove: (category) => `Rimuovi override ${category}`,
    fitOverridesPickerUnavailable: 'Apri questa pagina dall’Admin Shopify per scegliere le collezioni.',

    sizeGuidesTitle: 'Guide taglie',
    sizeGuidesDescription:
      'Carica una guida per categoria di prodotto. Il consiglio taglia usa la guida assegnata al capo provato; senza una guida utilizzabile, Trova la mia taglia spiega perché il consiglio non è disponibile.',
    sizeGuidesAddAction: 'Aggiungi una guida taglie',

    sizeGuideDropLabel: 'File della guida taglie',
    sizeGuideDropHint: 'PNG, JPEG, WebP o PDF, fino a 10 MB.',
    sizeGuideDropAction: 'Aggiungi file',
    sizeGuideReplaceTitle: 'Sostituisci la guida',
    sizeGuideRemove: 'Rimuovi guida',
    sizeGuideRemoving: 'Rimozione...',
    sizeGuideUploading: 'Stiamo leggendo la tua guida...',
    sizeGuideRowCount: (rows) =>
      rows === 1 ? '1 taglia letta dalla tua guida' : `${rows} taglie lette dalla tua guida`,
    sizeGuideShowAll: (rows) => `Mostra tutte le ${rows} taglie`,
    sizeGuideShowFewer: 'Mostra meno',
    sizeGuideSizeColumn: 'Taglia',
    sizeGuideEmpty:
      'Non siamo riusciti a leggere nemmeno una taglia da questo file. Controlla che la tabella sia leggibile e ricaricala, altrimenti la taglia consigliata resta spenta.',
    sizeGuideParsedAt: (date) => `Letta il ${date}`,
    sizeGuideSourceUnitCm: 'Letta in centimetri',
    sizeGuideSourceUnitIn: 'Letta in pollici, convertita in centimetri',
    sizeGuideBasisBody: 'Misure corporee',
    sizeGuideBasisGarment: 'Misure del capo',
    sizeGuideBasisUnknown: 'Misure di tipo non riconosciuto',
    garmentConverted: "Convertita dalle misure del capo",
    garmentExplanation: "Abbiamo ricavato gli intervalli corporei dalla tabella e dalla vestibilità del capo. Puoi modificare la vestibilità qui sotto.",
    garmentFit: "Vestibilità del capo",
    garmentSlim: "Aderente",
    garmentRegular: "Regolare",
    garmentOversize: "Oversize",
    garmentDefault: "Regolare per impostazione predefinita",
    garmentInferred: "Suggerita dal tuo negozio",
    garmentOverride: "Scelta da te",
    garmentReset: "Usa la vestibilità suggerita",
    garmentBodyTable: "Intervalli corporei stimati (cm)",
    garmentSourceTable: "Misure originali del capo (cm)",
    garmentShoulder: "La larghezza delle spalle non viene usata per consigliare la taglia di questo modello.",
    garmentLowConfidence: "Questi intervalli sono indicativi. Verifica che la vestibilità selezionata corrisponda ai tuoi prodotti.",
    garmentUnusable: "Non è stato possibile usare questa tabella per consigliare la taglia",
    garmentUnusableReason: "Non abbiamo individuato una misura di torace, vita o fianchi confrontabile con il corpo. Controlla le intestazioni e le unità della tabella.",
    garmentSaveFailure: "La modifica della vestibilità non è stata salvata. Le impostazioni precedenti sono ancora attive.",
    garmentPending: "La conversione non è momentaneamente disponibile. Riprova con l’anteprima della vestibilità.",
    garmentSave: "Salva vestibilità",
    garmentPreviewing: "Caricamento anteprima…",
    garmentPreviewReady: "Anteprima pronta. Salva per applicare questi intervalli.",
    garmentSuggestion: "Vestibilità oversize suggerita",
    garmentSuggestionReason: "Questa tabella è più ampia delle taglie standard. Verifica se i tuoi prodotti hanno una vestibilità oversize.",
    garmentApplySuggestion: "Anteprima vestibilità oversize",
    sizeGuideBasisBlockedTitle: 'Con questa guida la taglia consigliata resta spenta',
    sizeGuideBasisBlockedBody:
      'Confrontiamo la tua tabella con il corpo di chi acquista, quindi possiamo usare solo una tabella di misure corporee. Finché non ne carichi una, i clienti hanno la prova virtuale ma nessuna taglia consigliata.',
    sizeGuideBasisGarmentDetail:
      'Questa tabella sembra riportare le misure del capo: la larghezza del vestito steso, non del corpo che ci entra. Usata come misura corporea sposterebbe il consiglio di una o due taglie, su ogni prodotto, senza che nulla sembri sbagliato.',
    sizeGuideBasisUnknownDetail:
      'Non siamo riusciti a capire se questa tabella misuri il corpo o il capo. Cerca una tabella che dichiari di riportare misure corporee.',

    sizeGuideNameLabel: 'Nome della guida',
    sizeGuideNameSave: 'Salva nome',
    sizeGuideFallbackBadge: 'Guida predefinita',
    sizeGuideFallbackHint: 'Una guida senza categorie vale per tutti i prodotti che nessun’altra guida copre.',
    sizeGuideTypesLabel: 'Tipi di prodotto',
    sizeGuideTypesPlaceholder: 'es. Pantaloni',
    sizeGuideCollectionsLabel: 'Collezioni',
    sizeGuideTagsLabel: 'Tag',
    sizeGuideTagsPlaceholder: 'Invio per aggiungere un tag',

    sizeGuideErrorUnreadable:
      'Non siamo riusciti a estrarre la guida taglie in modo affidabile. Le guide esistenti non sono state modificate.',
    sizeGuideErrorInvalidFile: 'Il file non si apre come immagine o PDF valido. Esportalo nuovamente e riprova.',
    sizeGuideErrorImageUnreadable: 'L’immagine non è leggibile. Esportala nuovamente come PNG, JPEG o WebP.',
    sizeGuideErrorNoText: 'Il file si apre, ma non è stato trovato testo leggibile. Prova un PDF con testo selezionabile o un’esportazione con più contrasto.',
    sizeGuideErrorNoTable: 'Il file è leggibile, ma non è stata rilevata una struttura tabellare di taglie.',
    sizeGuideErrorCells: 'È stata rilevata una tabella, ma non è stato possibile estrarne le celle in modo affidabile.',
    sizeGuideErrorNoRows: 'È stata rilevata una tabella, ma non contiene righe taglia e misure utilizzabili.',
    sizeGuideAlternative: 'Puoi riprovare senza perdere la configurazione. Se il problema persiste, esporta un PDF con testo selezionabile o contatta il supporto con il riferimento qui sotto.',
    sizeGuideErrorReference: (id) => `Riferimento: ${id}`,
    sizeGuideErrorParserDown:
      'Il servizio che legge le tabelle taglie non è disponibile in questo momento. Le tue impostazioni non sono state toccate. Riprova tra qualche minuto.',
    sizeGuideErrorTooLarge: 'Il file supera i 10 MB. Prova con un\'immagine più leggera o un PDF più corto.',
    sizeGuideErrorUnsupportedType: 'Questo tipo di file non è supportato. Usa PNG, JPEG, WebP o PDF.',
    sizeGuideErrorGeneric: 'Non siamo riusciti a caricare la guida. Riprova.',
    sizeGuideProductsLabel: "Prodotti specifici",
    sizeGuideProductsHelp: "I prodotti hanno precedenza sugli altri criteri. Un prodotto può appartenere a una sola guida. Lascia tutti i criteri vuoti per una guida predefinita.",
    sizeGuideProductsUnavailable: "Prodotto non disponibile. Riprova o rimuovi il riferimento.",
    sizeGuideProductPublished: "Pubblicato",
    sizeGuideProductUnpublished: "Non pubblicato / non disponibile",
    sizeGuideErrorConflict: (name) => `Uno di questi valori è già assegnato a «${name}». Prima rimuovilo da lì.`,
    sizeGuideErrorSecondFallback: (name) => `«${name}» è già la guida predefinita. Assegna delle categorie a una delle due.`,
    sizeGuideErrorTooMany: (max) => `Puoi tenere al massimo ${max} guide taglie.`,
    sizeGuideErrorInvalidName: 'Il nome della guida non può essere vuoto.',
    sizeGuideErrorSettingsChanged:
      'Le tue impostazioni sono cambiate altrove mentre le stavi modificando. Ricarica la pagina e riprova.',

    sizeGuideCoverageOk: (matched, sampled) => `Le taglie combaciano con ${matched} prodotti su ${sampled} controllati.`,
    sizeGuideCoverageMismatch: (matched, sampled) =>
      `Le taglie combaciano solo con ${matched} prodotti su ${sampled} controllati. Sugli altri il consiglio taglia non comparirà. Confronta le etichette della guida con le taglie di quei prodotti.`,
    sizeGuideCoverageExamples: (titles) => `Per esempio: ${titles}`,
    sizeGuideCoverageUnchecked: 'Copertura non ancora controllata.',
    sizeGuideCoverageFailed: 'Non è stato possibile controllare la copertura. Verrà ritentato in automatico.',
    sizeCoverageCheckedAt: (date) => `Copertura controllata l'ultima volta il ${date}`,

    measurements: {
      height_cm: 'Altezza (cm)',
      chest_cm: 'Torace (cm)',
      underbust_cm: 'Sottoseno (cm)',
      waist_cm: 'Vita (cm)',
      hip_cm: 'Fianchi (cm)',
      inseam_cm: 'Cavallo (cm)',
      shoulder_width_cm: 'Larghezza spalle (cm)',
      sleeve_length_cm: 'Lunghezza manica (cm)',
      neck_cm: 'Collo (cm)',
      thigh_cm: 'Coscia (cm)',
      foot_length_cm: 'Lunghezza piede (cm)',
    },

    targetingTitle: 'Dove compaiono prova virtuale e taglia',
    targetingDescription:
      'Due regole indipendenti. I prodotti che una regola esclude non mostrano mai quella funzione e non consumano mai una prova.',
    targetingTryonTitle: 'Prova virtuale',
    targetingTryonDescription: 'Dove compare il pulsante di prova virtuale nel tuo negozio.',
    targetingSizeTitle: 'Taglia consigliata',
    targetingSizeDescription:
      'Dove viene proposta una taglia. Tienila sui prodotti che la tua guida taglie copre davvero.',
    targetingModeLabel: 'Mostra su',
    targetingModeAll: 'Tutti i prodotti',
    targetingModeInclude: 'Solo i prodotti che scelgo',
    targetingModeExclude: 'Tutti i prodotti tranne quelli che scelgo',
    targetingCollections: 'Collezioni',
    targetingProducts: 'Prodotti',
    targetingChooseCollections: 'Scegli collezioni',
    targetingChooseProducts: 'Scegli prodotti',
    targetingNoCollections: 'Nessuna collezione scelta.',
    targetingNoProducts: 'Nessun prodotto scelto.',
    targetingProductFallbackLabel: (id) => `Prodotto ${id}`,
    targetingPickerUnavailable:
      'Apri questa pagina dal tuo admin Shopify per scegliere collezioni e prodotti.',
    targetingPickerFailed:
      'Non siamo riusciti ad aggiungere tutto quello che hai scelto. Non è stato cambiato nulla. Riprova.',
    targetingCoverageAll: 'Mostrato su tutti i prodotti del negozio.',
    targetingCoverageIncludeEmpty:
      'Non hai scelto nulla, quindi non viene mostrato su nessun prodotto. Scegli almeno una collezione o un prodotto.',
    targetingCoverageInclude: (collections, products) =>
      `Mostrato solo sui prodotti delle ${collections} collezioni e sui ${products} prodotti scelti qui.`,
    targetingCoverageExcludeEmpty: 'Non escludi nulla, quindi viene mostrato su tutti i prodotti.',
    targetingCoverageExclude: (collections, products) =>
      `Nascosto sui prodotti delle ${collections} collezioni e sui ${products} prodotti scelti qui, mostrato ovunque altrove.`,
    targetingCoverageCaveat:
      'A quanti prodotti corrisponda dipende dal tuo catalogo, quindi qui non mostriamo un conteggio.',
    targetingLimitReached: (max) => `Puoi scegliere fino a ${max} voci per lista.`,
    targetingSyncPendingTitle: 'Il tuo negozio non è ancora allineato',
    targetingSyncPendingBody:
      'Le impostazioni sono salvate, ma la copia che legge il tuo negozio non è stata aggiornata. Finché non lo è, una pagina prodotto può mostrare ancora un pulsante che hai spento. Salva di nuovo per riprovare.',
    targetingSyncDetail: (reason) => `Shopify ha risposto: ${reason}`,
    targetingSyncedAt: (date) => `Negozio aggiornato il ${date}`,
  },

  onboarding: {
    title: 'Configura la Prova Virtuale',
    subtitle: 'Segui questi passaggi per attivare il widget di prova virtuale nel tuo negozio.',
    step1Title: '1. Verifica Compatibilit\u00E0 del Tema',
    step1Body: 'La prova virtuale richiede un tema Online Store 2.0. Verificheremo automaticamente.',
    step2Title: '2. Scegli un Piano',
    step2Body: 'Seleziona un piano di abbonamento adatto alle esigenze del tuo negozio.',
    step2Action: 'Scegli un Piano',
    step3Title: '3. Aggiungi il Widget al Tuo Tema',
    step3Body: 'Vai all\'editor del tema, naviga a un template di pagina prodotto e aggiungi il blocco app "Virtual Try-On". Puoi scegliere tra il widget completo o il solo pulsante.',
    step3Action: 'Apri Editor Tema',
    step4Title: '4. Personalizza l\'Aspetto',
    step4Body: 'Personalizza il colore del pulsante, il colore del testo e il limite per utente dalla pagina Impostazioni.',
    step4Action: 'Vai alle Impostazioni',
    step5Title: '5. Carica la guida taglie',
    step5Body:
      'Carica la tua tabella taglie per poter consigliare ai clienti quale taglia ordinare. Deve essere una tabella di misure corporee: ti mostriamo la tabella che abbiamo letto, così puoi controllarla prima che vada online.',
    step5GarmentBody:
      'Carica una tabella con le misure del corpo o del capo. Mostreremo le misure usate per i consigli di taglia.',
    step5Action: 'Carica una guida taglie',
    step5Done: 'Guida taglie caricata',
    step5NotBodyBasis:
      'La guida è caricata, ma i consigli di taglia non sono attivi per questa guida. Apri le Impostazioni per verificarne lo stato.',
    step6Title: '6. Scegli dove appaiono prova virtuale e taglia',
    step6Body:
      'Di default girano su tutti i prodotti. Restringile ai prodotti adatti: quello che resta fuori non mostra mai la funzione e non consuma mai una prova.',
    step6Action: 'Scegli i prodotti',
    watchVideo: 'Guarda il video tutorial',
    dismiss: 'Chiudi',
    showSetupGuide: 'Mostra guida di configurazione',
  },

  onboardingFlow: {
    dismiss: 'Salta per ora',
    stepOf: (current, total) => `Passo ${current} di ${total}`,
    productTitle: 'Provalo su di te — con un tuo prodotto',
    productSubtitle:
      'Abbiamo scelto un prodotto dal tuo negozio per la prova. Puoi passare a qualsiasi altro prodotto.',
    productContinue: 'Usa questo prodotto',
    productChange: 'Scegli un altro prodotto',
    productPickerTitle: 'Scegli un prodotto',
    productPickerClose: 'Chiudi',
    productRiskyBadge: 'Potrebbe essere rifiutato dalla moderazione',
    productNoImageBadge: 'Senza immagine',
    productRiskyOnlyWarning:
      'I prodotti trovati sono in categorie che il motore di prova spesso rifiuta (costumi da bagno, intimo). Puoi comunque sceglierne uno, ma il primo risultato potrebbe essere rifiutato.',
    productEmptyTitle: 'Nessun prodotto trovato',
    productEmptyBody:
      'Il tuo negozio non ha ancora prodotti attivi con un’immagine. Aggiungi un prodotto su Shopify e torna qui: la demo ha bisogno di un prodotto reale da provare.',
    productEmptyRefresh: 'Controlla di nuovo',
    productLoadFailed: 'Non siamo riusciti a caricare i tuoi prodotti. Riprova.',
    photoTitle: 'Aggiungi la tua foto',
    photoSubtitle: 'Alla demo basta una tua foto veloce.',
    photoGuide1: 'Figura intera, in piedi, rivolta verso la fotocamera',
    photoGuide2: 'Buona luce, sfondo semplice se possibile',
    photoGuide3: 'Solo tu nell’inquadratura',
    photoPrivacy:
      'La tua foto viene usata solo per generare questa prova. Non viene mai salvata sui nostri server e non esce da questa generazione.',
    photoSelect: 'Scegli una foto',
    photoReplace: 'Usa un’altra foto',
    photoConfirm: 'Genera la mia prova',
    photoPreviewAlt: 'Anteprima della tua foto',
    photoInvalidType: 'Scegli un’immagine JPEG, PNG o WebP.',
    photoTooLarge: 'La foto è troppo grande. Scegline una sotto i 20 MB.',
    photoReadError: 'Non siamo riusciti a leggere la foto. Provane un’altra.',
    photoBack: 'Torna al prodotto',
    genStagePhoto: 'Prepariamo la tua foto…',
    genStageProduct: (productTitle) => `Analizziamo ${productTitle}…`,
    genStageCreate: 'Creiamo la tua prova…',
    genHint: 'Di solito ci vogliono circa dieci secondi.',
    resultTitle: 'Eccolo — su di te',
    resultSubtitle: (productTitle) => `${productTitle}, dal tuo negozio, sulla tua foto.`,
    resultAlt: 'Il risultato della tua prova virtuale',
    resultOriginalAlt: 'La tua foto originale',
    resultShowOriginal: 'Mostra la mia foto',
    resultShowTryOn: 'Mostra la prova',
    resultRetryPhoto: 'Prova un’altra foto',
    resultRetryProduct: 'Prova un altro prodotto',
    resultCta: 'Mettilo online nel mio negozio',
    resultRemaining: (remaining) =>
      remaining === 1 ? '1 prova demo rimasta' : `${remaining} prove demo rimaste`,
    demoErrorTitle: 'Questa non è andata',
    demoErrorFlagged:
      'Il provider ha rifiutato questo prodotto — capita con costumi da bagno e categorie simili. La tua foto va bene.',
    demoErrorCannotGenerate:
      'Il motore non è riuscito a generare la prova per questo prodotto. Con un altro prodotto di solito funziona.',
    demoErrorInvalidPhoto: 'Non siamo riusciti a usare quella foto. Provane una diversa.',
    demoErrorPayloadTooLarge: 'La foto è troppo grande da elaborare. Scegline una più piccola.',
    demoErrorImageUnavailable:
      'Non siamo riusciti a caricare l’immagine del prodotto. Prova un altro prodotto o controlla l’immagine su Shopify.',
    demoErrorEngineUnavailable:
      'Il servizio di prova è momentaneamente non disponibile. Riprova tra poco.',
    demoErrorTimeout: 'Ci è voluto più del previsto. Riprova.',
    demoErrorAllowanceExhausted:
      'Hai usato tutte le prove demo per questo negozio. Il widget sul tuo storefront non è toccato.',
    demoErrorTooSoon: 'Una prova è già in corso. Aspetta qualche secondo.',
    demoErrorProductNotFound:
      'Quel prodotto non è più disponibile per la demo. Scegline un altro.',
    demoErrorUnexpected: 'Qualcosa è andato storto da parte nostra. Riprova.',
    demoKeepPhoto: 'La tua foto è ancora qui — non serve ricaricarla.',
    demoTryNextProduct: (productTitle) => `Riprova con “${productTitle}”`,
    demoRetry: 'Riprova',
    simTitle: 'Cosa misurerà Aisthetix per te',
    simSubtitle:
      'Quando il widget sarà attivo, ogni passo qui sotto diventerà un dato reale in questa dashboard. Ecco come apparirà, con il tuo prodotto.',
    simDisclaimer: 'Simulazione dimostrativa — questi non sono dati reali né una previsione.',
    simStepTryon: 'Prova virtuale completata',
    simStepCart: 'Aggiunto al carrello',
    simStepOrder: 'Ordine attribuito',
    simStepRevenue: 'Ricavo attribuito',
    simRevenueNote: 'Mostrato con il prezzo reale di questo prodotto.',
    simReplay: 'Rivedi',
    simCta: 'Inizia a raccogliere dati reali',
    simBack: 'Torna alla mia prova',
    actTitle: 'Ultimo passo: metti il widget nel tuo negozio',
    actBody:
      'L’editor del tema si aprirà con il blocco Aisthetix già aggiunto alla pagina prodotto. Controlla dove si trova, poi premi Salva.',
    actManualNote:
      'Shopify richiede che sia tu a premere Salva — è l’unico passo che non possiamo fare al posto tuo.',
    actOpenEditor: 'Apri l’editor del tema',
    actAutoCheckNote: 'Rileviamo il salvataggio automaticamente — torna su questa scheda quando hai finito.',
    actManualCheck: 'Ho salvato — verifica ora',
    actChecking: 'Controlliamo il tuo tema…',
    actDisabledOnly:
      'Il blocco è nel tema ma al momento è disattivato. Attivalo nell’editor del tema e salva.',
    actVerifiedTitle: 'Il widget è attivo',
    actVerifiedBody: (themeName) => `Abbiamo trovato il blocco Aisthetix in “${themeName}”. Hai finito.`,
    actDone: 'Vai alla mia dashboard',
    actBack: 'Indietro',
  },

  progressiveDashboard: {
    activationPendingTitle: 'Il widget non è ancora sul tuo storefront',
    activationPendingBody:
      'I clienti non possono vedere la prova finché il blocco app non è salvato nel tema. Ci vuole un minuto.',
    activationPendingAction: 'Apri l’editor del tema',
    activationCheck: 'Controlla di nuovo',
    waitingTitle: 'In attesa del primo cliente',
    waitingBody:
      'Il widget è attivo. Appena un cliente prova un capo, i numeri reali sostituiscono questa scheda.',
    replaySimulation: 'Rivedi l’esempio',
    viewMyTryOn: 'Vedi la mia prova',
    myTryOnTitle: 'La tua prova',
    firstTryonTitle: 'Il tuo negozio ha servito la prima prova 🎉',
    firstTryonBody: 'I clienti la stanno usando. Ecco cosa è successo finora.',
    tryonsServed: (count) => (count === 1 ? '1 prova servita' : `${count} prove servite`),
    cartsTitle: 'Aggiunte al carrello dopo una prova',
    ordersTitle: 'Ordini attribuiti',
    revenueTitle: 'Ricavo attribuito',
    lockedUntilData: 'Si sblocca con i primi dati',
    viewAnalytics: 'Apri le analytics',
    tryDemoTitle: 'Prova la nuova demo della dashboard',
    tryDemoBody:
      'Guarda una prova virtuale generata con un tuo prodotto e una tua foto, direttamente qui nella dashboard.',
    tryDemoAction: 'Provala su di me',
  },

  themeCompatibility: {
    checking: 'Verifica compatibilit\u00E0 del tema\u2026',
    compatible: (themeName) => `Il tuo tema "${themeName}" supporta la prova virtuale.`,
    incompatibleTitle: 'Tema non compatibile rilevato',
    incompatibleBody: (themeName) =>
      `Il tuo tema attuale${themeName ? ` "${themeName}"` : ''} non supporta i blocchi app (richiesto Online Store 2.0). Passa a un tema compatibile prima di abbonarti.`,
    incompatibleSubscribeWarning:
      'Il tuo tema attuale non supporta i blocchi app. Passa a un tema Online Store 2.0 prima di abbonarti.',
  },

  trial: {
    lowTitle: 'La tua prova gratuita sta per finire',
    lowBody: (remaining, cap) =>
      `Hai usato ${cap - remaining} delle ${cap} prove incluse nel periodo di prova. Quando finiscono, il pulsante della prova virtuale viene nascosto dal tuo negozio finché non parte il piano.`,
    exhaustedTitle: 'Prove del periodo di prova esaurite',
    exhaustedBody: (cap, trialEndsOn) =>
      `Hai usato tutte le ${cap} prove del periodo di prova, quindi il pulsante è nascosto dal tuo negozio. Attiva il piano per riaccenderlo subito, oppure aspetta: parte da solo il ${trialEndsOn}.`,
    startPlan: (planName) => `Attiva ora ${planName}`,
    approvalNote:
      'Shopify ti chiederà di approvare l’addebito. Non viene addebitato nulla finché non lo fai.',
    failed: 'Non siamo riusciti ad attivare il piano. Riprova.',
  },
};
